import React, { useState, useEffect } from "react";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { tp_logo } from "../../constants/icons";
import { transferPointsService } from "../../services/api";
import api from "../../services/api";
const MyTransferPoints = () => {
  // State for API data
  const [transferPointsData, setTransferPointsData] = useState({
    availablePoints: 0,
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Fetch transfer points balance and history
  useEffect(() => {
    const fetchTransferPointsData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch balance
        const balanceResponse = await transferPointsService.getBalance();
        if (balanceResponse.success) {
          setTransferPointsData(prev => ({
            ...prev,
            availablePoints: balanceResponse.data.balance || 0,
          }));
        } else {
          console.error("Error fetching balance:", balanceResponse.error);
        }

        // Fetch history
        const historyResponse = await transferPointsService.getHistory();
        if (historyResponse.success) {
          // Transform API data to match UI format.
          // NOTE: /users/transfer-points-history/ returns the ledger UNORDERED
          // (no Meta.ordering, no .order_by), which in practice means insert
          // order = oldest first — a fresh entry landed at the very bottom of
          // the list. We keep the raw timestamp as `sortKey` (the formatted
          // date is a display string and cannot be sorted) and order the rows
          // newest-first here. Unparseable timestamps sort last.
          const transformedHistory = historyResponse.data
            .map(item => {
              // Parse the ISO timestamp properly
              let formattedDate = "Invalid Date";
              let sortKey = 0;
              try {
                const date = new Date(item.created_at);
                if (!isNaN(date.getTime())) {
                  sortKey = date.getTime();
                  formattedDate = date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  });
                }
              } catch (error) {
                console.error("Error parsing date:", item.created_at, error);
              }

              return {
                id: item.id,
                sortKey,
                date: formattedDate,
                description: item.description,
                points: item.points,
                type: item.transaction_type === "earned" ? "credit" : "deduction",
              };
            })
            // Newest first; same-timestamp entries fall back to the ledger id
            // (also monotonic), so the order is stable.
            .sort((a, b) => (b.sortKey - a.sortKey) || (b.id - a.id));
          setTransactionHistory(transformedHistory);
        } else {
          console.error("Error fetching history:", historyResponse.error);
        }
      } catch (err) {
        setError("Failed to load transfer points data. Please try again.");
        console.error("Transfer Points API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransferPointsData();
  }, []);

  // Function to get random point background colors
  const getRandomPointBGColor = () => {
    const colors = [
      "bg-gradient-to-b from-[rgba(246,167,85,0.5)] via-[rgba(236,164,90,0.5)] to-[rgba(255,173,88,0.5)]",
      "bg-gradient-to-b from-[rgba(214,219,234,0.5)] via-[rgba(210,211,213,0.5)] to-[rgba(179,179,180,0.5)]",
      "bg-gradient-to-b from-[rgba(246,128,85,0.5)] via-[rgba(236,143,90,0.5)] to-[rgba(208,75,3,0.5)]",
      "bg-gradient-to-b from-[rgba(126,139,255,0.5)] to-[rgba(34,131,255,0.5)]",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Function to fetch leaderboard data
  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const response = await api.get("/users/tp-leader-board/");
      if (response.data && response.data.top_clubs) {
        setLeaderboardData(response.data.top_clubs);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Function to handle view leaderboard button click
  const handleViewLeaderboard = () => {
    setShowLeaderboard(true);
    if (leaderboardData.length === 0) {
      fetchLeaderboard();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="sm:px-14 px-4 mx-auto p-6 space-y-8">
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="sm:px-14 px-4 mx-auto p-6 space-y-8">
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <p className="text-red-500 mb-2">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="text-blue-500 hover:text-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sm:px-14 px-4 mx-auto p-6 space-y-8">
        {/* TransferPoints Summary Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">
              MY TRANSFERPOINTS
            </h2>
          </div>

          <div className="flex gap-6 justify-between flex-col sm:flex-row ">
            {/* Available TP Points Card */}
            <div className="flex gap-4 flex-col sm:flex-row">
              <div className=" bg-[#F8F9FB] p-4 rounded-xl sm:w-fit w-full">
                <div className="flex items-center gap-3 ">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center">
                    <img
                      src={tp_logo}
                      alt="tp_logo"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-[16px] text-[#43526E] font-medium">
                      Available TP Points
                    </p>
                  </div>
                  <div>
                    <p className="text-[16px] font-bold text-[#1A212F]">
                      {transferPointsData.availablePoints} pts
                    </p>
                  </div>
                </div>
              </div>

              {/* "Overall Club Ranking" tile removed: it rendered a hardcoded
                  0 that no API ever wrote to. There is no ranking field on any
                  backend payload (no overall_ranking / club_ranking anywhere),
                  and /users/tp-leader-board/ only returns the top N clubs with
                  a balance > 0 — it cannot yield this club's own position in
                  the general case. Rather than show a meaningless zero we hide
                  the figure; the "View Leaderboard" link beside it is the real
                  standings surface. Restore when the backend exposes a rank. */}
            </div>

            <Link
              to="/leaderboard?type=clubs"
              className="text-[#2283FF] hover:text-blue-800 text-sm font-medium"
            >
              View Leaderboard
            </Link>
          </div>
        </div>

        {/* Points History Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <h2 className="text-[11px] font-bold text-[#3C3C3C] mb-6">
            POINTS HISTORY
          </h2>

          <div className="space-y-4">
            {transactionHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm">No transaction history available</p>
              </div>
            ) : (
              transactionHistory.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between py-3 bg-[#F8F9FB] px-4 border-b border-gray-100 last:border-b-0 flex-col sm:flex-row gap-2"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#1A212F] font-medium min-w-[100px]">
                        {transaction.date}
                      </span>
                      <span className="text-sm text-[#1A212F] flex-1">
                        {transaction.description}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end">
                    {/* Signed amount: the caret alone carried the earned/spent
                        distinction, the number itself was unsigned. Same
                        red/green tokens as the caret below. */}
                    <span
                      className={`text-sm font-medium ${
                        transaction.type === "deduction"
                          ? "text-red-500"
                          : "text-green-500"
                      }`}
                    >
                      {transaction.type === "deduction" ? "-" : "+"}
                      {transaction.points} Pts
                    </span>
                    {transaction.type === "deduction" ? (
                      <FaCaretDown className="text-red-500 text-lg" />
                    ) : (
                      <FaCaretUp className="text-green-500 text-lg" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>


    </>
  );
};

export default MyTransferPoints;
